/* ──────────────────────────────────────────────────────────────
   VacacionesTimeline   (escala = días)
   Ventana visible: hoy → +3 meses
   Muestra:
      • Vacaciones   – completo / mañana / tarde
      • Ausencias    – día completo / parcial horas
   ────────────────────────────────────────────────────────────── */

// @ts-ignore – los estilos los inyecta el paquete
import "react-calendar-timeline/dist/style.css";

import { memo, useEffect, useMemo } from "react";
import Timeline, {
  TimelineHeaders,
  DateHeader,
  TimelineGroupBase,
  TimelineItemBase,
} from "react-calendar-timeline";
import dayjs from "dayjs";
import "dayjs/locale/es";
import "dayjs/locale/en";

import { Box, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

/* ═════════ tipos (informe agenda-profesor) ═════════ */
export type AgendaDetRow = {
  fecha: string; // «YYYY-MM-DD»
  kind: "vac" | "aus";
  /* VAC */
  tramo?: "completo" | "manana" | "tarde";
  /* AUS */
  tipoAbs?: "justificada" | "injustificada";
  motivo?: string | null;
  horaIni?: string | null; // «HH:mm»
  horaFin?: string | null;
};

export type AgendaProfRow = {
  profesorId: number;
  profesor: string;
  detalle: AgendaDetRow[];
  /* filtros opcionales */
  tipoId?: number;
  centroId?: number;
};

interface Props {
  rows: AgendaProfRow[];
  search: string;
}

/* — helper para medias‑jornadas — */
const halfStyle = (turno: "manana" | "tarde"): React.CSSProperties => ({
  height: "50%",
  top: turno === "tarde" ? "50%" : 0,
  position: "absolute",
});

/* ═════════════════ componente ══════════════════════ */
function AgendaTimeline({ rows, search }: Props) {
  const { i18n } = useTranslation<"translation">();
  const theme = useTheme();

  /* sincroniza locale de Day.js con la IU */
  useEffect(() => {
    dayjs.locale(i18n.language);
  }, [i18n.language]);

  /* parámetros globales */
  const ONE_DAY = 86_400_000; // ms en 24 h
  const CELL_PX = 60; // ancho de 1 día

  const today = dayjs().startOf("day");
  const visStart = today.valueOf();
  const visEnd = today.add(3, "month").endOf("day").valueOf();
  const canvasW = Math.ceil((visEnd - visStart) / ONE_DAY) * CELL_PX;

  /* ═══ transforma datos → groups + items ═══ */
  const { groups, items } = useMemo(() => {
    const q = search.toLowerCase();

    const gs: TimelineGroupBase[] = [];
    const its: TimelineItemBase<number>[] = [];

    rows
      .filter((r) => r.profesor.toLowerCase().includes(q))
      .forEach(({ profesor, detalle }, gi) => {
        /* —— group —— */
        gs.push({ id: gi, title: profesor });

        /* —— items —— */
        detalle.forEach((d, di) => {
          /* ① rango temporal */
          const base = dayjs(d.fecha);

          let start = base;
          let end = base.add(1, "day"); // valor por defecto

          if (d.kind === "vac") {
            // medias jornadas = 12 h
            if (d.tramo === "manana") end = base.add(12, "hour");
            if (d.tramo === "tarde") start = base.add(12, "hour");
          }

          if (d.kind === "aus") {
            // ausencias parciales: HH:mm → HH:mm
            if (d.horaIni) start = dayjs(`${d.fecha}T${d.horaIni}`);
            if (d.horaFin) end = dayjs(`${d.fecha}T${d.horaFin}`);

            // filtro “sanidad”: si la ausencia no tiene duración real, ignórala
            if (!d.horaIni && !d.horaFin && d.motivo == null) return;
          }

          /* ② colores */
          let bg = theme.palette.info.light; // vacación día completo
          let edge = theme.palette.info.main;

          if (d.kind === "vac" && d.tramo !== "completo") {
            bg = theme.palette.info.main;
            edge = theme.palette.info.dark;
          }

          if (d.kind === "aus") {
            const ok = d.tipoAbs === "justificada";
            bg = ok ? theme.palette.success.light : theme.palette.error.light;
            edge = ok ? theme.palette.success.dark : theme.palette.error.dark;
          }

          /* ③ tooltip */
          const tip =
            d.kind === "vac"
              ? `Vacaciones · ${d.tramo ?? "completo"}`
              : `Ausencia (${d.tipoAbs})${d.motivo ? ` · ${d.motivo}` : ""}`;

          its.push({
            id: gi * 10_000 + di,
            group: gi,
            title: tip,
            start_time: start.valueOf(),
            end_time: end.valueOf(),

            // ───── propiedades extra para el <div> del ítem ─────
            itemProps: {
              "data-tramo": d.tramo ?? "completo", // atributo-data personalizado
              style: {
                background: bg,
                border: `1px solid ${edge}`,
                borderRadius: 4,
              },
            } as React.HTMLAttributes<HTMLDivElement> & {
              ["data-tramo"]: string;
            },
            //      ↑↑↑ puedes simplificarlo a “as any” si prefieres
          });
        });
      });

    return { groups: gs, items: its };
  }, [rows, search, theme.palette]);

  /* ═══ render ═══ */
  return (
    <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
      <Timeline
        groups={groups}
        items={items}
        keys={{
          groupIdKey: "id",
          groupTitleKey: "title",
          groupLabelKey: "title",
          groupRightTitleKey: "title",
          itemIdKey: "id",
          itemGroupKey: "group",
          itemTitleKey: "title",
          itemDivTitleKey: "title",
          itemTimeStartKey: "start_time",
          itemTimeEndKey: "end_time",
        }}
        defaultTimeStart={visStart}
        defaultTimeEnd={visEnd}
        minZoom={ONE_DAY}
        maxZoom={dayjs().add(6, "month").endOf("day").valueOf() - visStart}
        sidebarWidth={220}
        lineHeight={38}
        stackItems
        canMove={false}
        canResize={false}
        timeSteps={{ second: 1, minute: 1, hour: 1, day: 1, month: 1, year: 1 }}
        style={{ width: canvasW }}
        itemRenderer={({ item, getItemProps }) => {
          const tramo = (item as any)["data-tramo"] as "completo" | "manana" | "tarde";
          const extra = tramo === "manana" || tramo === "tarde" ? halfStyle(tramo) : {};
          return (
            <Tooltip title={item.title as string}>
              <div
                {...getItemProps({
                  style: { ...item.itemProps?.style, ...extra },
                })}
              />
            </Tooltip>
          );
        }}
      >
        <TimelineHeaders>
          <DateHeader
            unit="day"
            labelFormat={(ts) => dayjs(Array.isArray(ts) ? ts[0] : ts).format("D MMM")}
          />
        </TimelineHeaders>
      </Timeline>
    </Box>
  );
}

export default memo(AgendaTimeline);
